export default class ArchText {
  /**
   * @param {string} selector
   * @param {{ arcAngle?: number, radiusRatio?: number }} options
   */
  constructor(selector = '.p-index__class_band_bottom_intro', options = {}) {
    this.selector = selector;
    this.arcAngle = options.arcAngle ?? 28;
    this.radiusRatio = options.radiusRatio ?? 1.85;
    this.elements = [...document.querySelectorAll(selector)];
    this.resizeTimer = null;

    if (!this.elements.length) return;

    this.elements.forEach((el) => this.setup(el));
    this.bindResize();

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        this.elements.forEach((el) => this.layout(el));
      });
    }
  }

  setup(el) {
    if (el.dataset.archReady === 'true') {
      this.layout(el);
      return;
    }

    const text = (el.textContent || '').replace(/\s+/g, '').trim();
    if (!text) return;

    el.dataset.archText = text;
    el.setAttribute('aria-label', text);
    el.replaceChildren();

    Array.from(text).forEach((char) => {
      const span = document.createElement('span');
      span.className = 'p-index__class_band_bottom_intro_char';
      span.setAttribute('aria-hidden', 'true');
      span.textContent = char;
      el.appendChild(span);
    });

    el.dataset.archReady = 'true';
    this.layout(el);
  }

  layout(el) {
    const spans = el.querySelectorAll('.p-index__class_band_bottom_intro_char');
    const count = spans.length;
    if (!count) return;

    const width = el.clientWidth || el.parentElement?.clientWidth || 0;
    if (!width) return;

    const isPc = window.matchMedia('(min-width: 768px)').matches;
    const fontSize = parseFloat(getComputedStyle(el).fontSize) || 16;

    // 文字サイズ基準で弧上の間隔を決める（PCはSPより少し詰める）
    const charStep = fontSize * (isPc ? 0.96 : 1.06);
    const arcLength = Math.max(count - 1, 1) * charStep;
    // 半径を大きくしてアーチを弱める
    const radiusRatio = isPc ? 2.55 : this.radiusRatio;
    const radius = Math.max(width * radiusRatio, 140);
    let totalAngle = (arcLength / radius) * (180 / Math.PI);
    totalAngle = Math.min(34, Math.max(10, totalAngle));

    const start = -totalAngle / 2;
    const step = count === 1 ? 0 : totalAngle / (count - 1);
    const halfRad = (totalAngle / 2) * (Math.PI / 180);
    const arcHeight = radius * (1 - Math.cos(halfRad));
    const boxHeight = Math.ceil(arcHeight + fontSize * 1.35);

    // 文字は origin から radius 上にあるため、origin をボックス下より下にずらして
    // 弧全体がボックス内に収まるようにする
    const originOffset = Math.ceil(radius - boxHeight + fontSize * 0.15);

    el.style.height = `${boxHeight}px`;

    spans.forEach((span, index) => {
      const angle = start + step * index;
      span.style.setProperty('--arch-angle', `${angle}deg`);
      span.style.height = `${radius}px`;
      span.style.bottom = `${-originOffset}px`;
    });
  }

  bindResize() {
    const onResize = () => {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.elements.forEach((el) => this.layout(el));
      }, 100);
    };

    window.addEventListener('resize', onResize);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(onResize);
      this.elements.forEach((el) => this.resizeObserver.observe(el));
    }
  }
}
